<template>
  <a-modal v-if="visible" title="Export Associate" :visible="visible" @cancel="closeModal">
    <div class="flex flex-column gap-2">
      <a-tree
        v-model="checkedKeys"
        checkable
        :expanded-keys="expandedKeys"
        :auto-expand-parent="autoExpandParent"
        :selected-keys="selectedKeys"
        :tree-data="treeData"
        @expand="onExpand"
        @select="selectedKeys = $event"
      />
      <b>Specific Company Documents</b>
      <document-search :value="form.company_documents" :multiply="true" @update="form.company_documents = $event" />
      <b>Specific Attached Documents</b>
      <attached-search :value="form.templates" :multiply="true" @update="form.templates = $event" />
      <b>Specific Trainings</b>
      <sequence-search
        :value="form.trainings"
        :multiply="true"
        :filters="{ type: 'training' }"
        @update="form.trainings = $event"
      />
    </div>

    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closeModal">Close</a-button>
        <export-button :loading="loadingExport" @send="sendReport" @download="downloadReport" />
      </div> </template
  ></a-modal>
</template>

<script>
import ExportButton from '@/components/export-button.vue'
import AttachedCreateSearch from '@/modules/document/components/attached/attached-create-search.vue'
import DocumentSearch from '@/modules/document/components/document-search.vue'
import SequenceSearch from '@/modules/sequence/components/sequence-search.vue'
import util from '@/util'
import { deepCopy } from '@/utils/deep-copy'
import { Button, notification, Tree } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'

const { mapActions: profileActions, mapState: profileState } = createNamespacedHelpers('profile')

const treeData = [
  {
    title: 'Export All',
    key: 'all',
    children: [
      {
        title: 'Profile',
        key: 'profile'
      },
      {
        title: 'Orientation',
        key: 'orientation'
      },
      {
        title: 'Onboarding',
        key: 'onboarding'
      },
      {
        title: 'Trainings',
        key: 'trainings'
      },
      {
        title: 'Company Documents',
        key: 'company_documents'
      },
      {
        title: 'Attached Documents',
        key: 'attached_documents'
      },
      {
        title: 'Written Warnings',
        key: 'write_ups'
      },
      {
        title: 'Departure',
        key: 'departure'
      }
    ]
  }
]

const initialForm = { templates: [], company_documents: [], trainings: [] }

export default {
  components: {
    'a-button': Button,
    'a-tree': Tree,
    'document-search': DocumentSearch,
    'attached-search': AttachedCreateSearch,
    'sequence-search': SequenceSearch,
    'export-button': ExportButton
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    initialSection: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      form: deepCopy(initialForm),

      loadingExport: false,

      selectedKeys: [],
      expandedKeys: ['all'],
      checkedKeys: [],
      autoExpandParent: true,

      treeData
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate
    }),

    documents() {
      return this.checkedKeys
        .filter(item => !['all'].includes(item))
        .reduce((acc, curr) => {
          acc[curr] = true

          return acc
        }, {})
    }
  },

  watch: {
    visible(value) {
      if (value && this.initialSection) {
        this.checkedKeys = [this.initialSection]
      }
    }
  },

  methods: {
    ...profileActions(['sendExportedAssociate', 'downloadExportedAssociate']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    closeModal() {
      this.$emit('update:visible', false)
      this.form = deepCopy(initialForm)
      this.expandedKeys = ['all']

      if (this.initialSection) {
        this.checkedKeys = []
      }
    },

    onExpand(expandedKeys) {
      this.expandedKeys = expandedKeys
      this.autoExpandParent = false
    },

    getExportPayload(email = null) {
      const { templates, trainings, company_documents } = this.form
      const hasTemplates = templates.length > 0
      const hasTrainings = trainings.length > 0
      const hasCompanyDocs = company_documents.length > 0

      const payload = {
        associate_id: this.associate.id,
        filters: {
          ...this.documents,
          template_ids: templates.map(item => item.id),
          company_document_ids: company_documents.map(item => item.id),
          training_ids: trainings.map(item => item.id),
          trainings: hasTrainings || this.checkedKeys.includes('trainings'),
          attached_documents: hasTemplates || this.checkedKeys.includes('attached_documents'),
          company_documents: hasCompanyDocs || this.checkedKeys.includes('company_documents')
        }
      }

      if (email) {
        payload.email = email.toLowerCase()
      }

      return payload
    },

    async sendReport(email) {
      this.loadingExport = true

      const payload = this.getExportPayload(email)

      try {
        await this.sendExportedAssociate(payload)

        this.showNotification('success', 'Success', 'Exported Associate Profile successfully sent to email')
      } catch (error) {
        this.showNotification('error', 'Error', error.message || error)
      } finally {
        this.loadingExport = false
      }
    },

    async downloadReport() {
      this.loadingExport = true

      const payload = this.getExportPayload()

      try {
        const url = await this.downloadExportedAssociate(payload)

        util.downloadFile(url)
      } catch (error) {
        this.showNotification('error', 'Error', error.message || error)
      } finally {
        this.loadingExport = false
      }
    }
  }
}
</script>
